// ============================================
// Portfolio Service - Won domains management
// ============================================

import { WonDomain, PortfolioStats, DomainCategory } from "../types";
import { GeminiService } from "./geminiService";
import { storageService } from "./storageService";

/**
 * PortfolioService
 *
 * Manages portfolio of won domains with:
 * - CRUD operations
 * - AI valuations
 * - ROI calculations
 * - Category tracking
 */
export class PortfolioService {
  private geminiService: GeminiService;
  private username: string;

  constructor(geminiService: GeminiService, username: string) {
    this.geminiService = geminiService;
    this.username = username;
  }

  /**
   * Get all portfolio domains
   */
  getDomains(): WonDomain[] {
    return storageService.getUserPortfolio(this.username);
  }

  /**
   * Add a won domain to portfolio
   */
  addDomain(domain: Omit<WonDomain, "id">): WonDomain {
    const newDomain: WonDomain = {
      ...domain,
      id: `dom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    const portfolio = this.getDomains();
    portfolio.push(newDomain);
    storageService.saveUserPortfolio(this.username, portfolio);

    return newDomain;
  }

  /**
   * Update a domain
   */
  updateDomain(domainId: string, updates: Partial<WonDomain>): WonDomain | null {
    const portfolio = this.getDomains();
    const index = portfolio.findIndex((d) => d.id === domainId);

    if (index === -1) return null;

    portfolio[index] = { ...portfolio[index], ...updates };
    storageService.saveUserPortfolio(this.username, portfolio);

    return portfolio[index];
  }

  /**
   * Delete a domain
   */
  deleteDomain(domainId: string): boolean {
    const portfolio = this.getDomains();
    const filtered = portfolio.filter((d) => d.id !== domainId);

    if (filtered.length === portfolio.length) return false;

    storageService.saveUserPortfolio(this.username, filtered);
    return true;
  }

  /**
   * Mark domain as sold
   */
  markAsSold(domainId: string, soldPrice: number): WonDomain | null {
    return this.updateDomain(domainId, {
      status: "sold",
      soldPrice,
      soldDate: new Date().toISOString(),
    });
  }

  /**
   * Get AI valuation for a domain
   */
  async getValuation(domainId: string): Promise<WonDomain | null> {
    const portfolio = this.getDomains();
    const domain = portfolio.find((d) => d.id === domainId);

    if (!domain) return null;

    try {
      const analysis = await this.geminiService.analyzeDomain(
        domain.domain,
        domain.purchasePrice
      );

      // Parse valuation string to number
      const valuationStr = analysis.valuation || "";
      const valuationMatch = valuationStr.match(/\$?([\d,]+)/);
      const valuation = valuationMatch
        ? parseInt(valuationMatch[1].replace(/,/g, ""), 10)
        : null;

      return this.updateDomain(domainId, {
        aiValuation: valuation,
        valuationDate: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Valuation failed:", error);
      return null;
    }
  }

  /**
   * Batch valuate all domains without recent valuations
   */
  async batchValuate(maxDomains: number = 10): Promise<number> {
    const portfolio = this.getDomains();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const needsValuation = portfolio.filter((d) => {
      if (!d.valuationDate) return true;
      return new Date(d.valuationDate) < thirtyDaysAgo;
    });

    let valuated = 0;
    for (const domain of needsValuation.slice(0, maxDomains)) {
      try {
        await this.getValuation(domain.id);
        valuated++;
        // Rate limiting
        await new Promise((r) => setTimeout(r, 500));
      } catch (error) {
        console.error(`Failed to valuate ${domain.domain}:`, error);
      }
    }

    return valuated;
  }

  /**
   * Calculate portfolio statistics
   */
  getStats(categories: DomainCategory[]): PortfolioStats {
    const portfolio = this.getDomains();

    const stats: PortfolioStats = {
      totalDomains: portfolio.length,
      totalInvested: 0,
      currentValue: 0,
      unrealizedGain: 0,
      roiPercentage: 0,
      domainsByCategory: {},
    };

    portfolio.forEach((domain) => {
      // Total invested
      stats.totalInvested += domain.purchasePrice;

      // Current value (use AI valuation or purchase price)
      const value = domain.status === "sold"
        ? domain.soldPrice || 0
        : domain.aiValuation || domain.purchasePrice;
      stats.currentValue += value;

      // Category tracking
      const category = this.findCategoryForDomain(domain.domain, categories);
      const catName = category?.name || "Uncategorized";
      stats.domainsByCategory[catName] = (stats.domainsByCategory[catName] || 0) + 1;
    });

    // Calculate gains
    stats.unrealizedGain = stats.currentValue - stats.totalInvested;
    stats.roiPercentage = stats.totalInvested > 0
      ? ((stats.currentValue - stats.totalInvested) / stats.totalInvested) * 100
      : 0;

    return stats;
  }

  /**
   * Find category for a domain
   */
  private findCategoryForDomain(domain: string, categories: DomainCategory[]): DomainCategory | null {
    const lowerDomain = domain.toLowerCase();
    for (const cat of categories) {
      for (const keyword of cat.keywords) {
        if (lowerDomain.includes(keyword)) {
          return cat;
        }
      }
    }
    return null;
  }

  /**
   * Get domains by status
   */
  getDomainsByStatus(status: WonDomain["status"]): WonDomain[] {
    return this.getDomains().filter((d) => d.status === status);
  }

  /**
   * Get top performers (highest ROI)
   */
  getTopPerformers(limit: number = 5): WonDomain[] {
    return this.getDomains()
      .filter((d) => d.aiValuation && d.aiValuation > d.purchasePrice)
      .sort((a, b) => {
        const roiA = ((a.aiValuation || 0) - a.purchasePrice) / a.purchasePrice;
        const roiB = ((b.aiValuation || 0) - b.purchasePrice) / b.purchasePrice;
        return roiB - roiA;
      })
      .slice(0, limit);
  }

  /**
   * Get domains needing attention (low valuation)
   */
  getUnderperformers(limit: number = 5): WonDomain[] {
    return this.getDomains()
      .filter((d) => d.aiValuation && d.aiValuation < d.purchasePrice)
      .sort((a, b) => {
        const roiA = ((a.aiValuation || 0) - a.purchasePrice) / a.purchasePrice;
        const roiB = ((b.aiValuation || 0) - b.purchasePrice) / b.purchasePrice;
        return roiA - roiB;
      })
      .slice(0, limit);
  }

  /**
   * Export portfolio as CSV
   */
  exportAsCSV(): string {
    const portfolio = this.getDomains();
    const headers = [
      "Domain",
      "Won Date",
      "Purchase Price",
      "AI Valuation",
      "Status",
      "Sold Price",
      "Sold Date",
      "Category",
      "Notes",
    ];

    const rows = portfolio.map((d) => [
      d.domain,
      d.wonDate,
      d.purchasePrice,
      d.aiValuation || "",
      d.status,
      d.soldPrice || "",
      d.soldDate || "",
      d.category,
      `"${d.notes.replace(/"/g, '""')}"`,
    ]);

    return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  }

  /**
   * Import from auction history
   */
  async importFromHistory(
    history: Array<{ domain: string; amount: number; date: string }>,
    category: string
  ): Promise<number> {
    let imported = 0;
    const existingDomains = new Set(this.getDomains().map((d) => d.domain.toLowerCase()));

    for (const item of history) {
      if (existingDomains.has(item.domain.toLowerCase())) continue;

      this.addDomain({
        domain: item.domain,
        wonDate: item.date,
        purchasePrice: item.amount,
        aiValuation: null,
        valuationDate: null,
        category,
        status: "owned",
        notes: "Imported from auction history",
        soldPrice: null,
        soldDate: null,
      });

      imported++;
    }

    return imported;
  }
}

// Factory function
export function createPortfolioService(
  geminiService: GeminiService,
  username: string
): PortfolioService {
  return new PortfolioService(geminiService, username);
}
